/* The authenticated shell: sidebar + (topbar + routed surface) + red-alert
   ribbon + Talk-to-AI dock + command palette + notifications + the New-build
   dispatch. Fetches the cross-cutting sources once here (react-query dedupes
   with the surfaces) and feeds them down. Redirects to /login when there's
   no session. */
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileNav } from './MobileNav';
import { AlertRibbon } from './AlertRibbon';
import { TalkDock } from './TalkDock';
import { CommandPalette, type PaletteAction } from './CommandPalette';
import { NewBuildDialog } from './NewBuildDialog';
import { NotificationsBell } from './NotificationsBell';
import { PwaNudge } from './PwaNudge';
import { useSession } from '../session';
import { useOps } from '../ops';
import { useChrome } from '../chrome';
import { WidgetEditProvider, useWidgetEdit } from '../widgets';
import { useIsMobile } from '../useBreakpoint';
import { useContract } from '../../lib/api';
import { labelForPath } from '../nav';
import { deriveDailyBrief } from '../../lib/selectors';
import type { HealthTile } from '../../lib/contract';
import { PLACEHOLDER } from '../../lib/placeholder';

function ShellInner() {
  const { user, signOut } = useSession();
  const { pushToast } = useOps();
  const { toggleTheme, theme } = useChrome();
  const { setEditing } = useWidgetEdit();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [talkOpen, setTalkOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [buildOpen, setBuildOpen] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(false);

  const health = useContract('health', { pollMs: 20_000 });
  const crew = useContract('crew', { pollMs: 20_000 });
  const reviews = useContract('reviews', { pollMs: 60_000 });
  const chat = useContract('chat', { pollMs: 0 });
  // Cross-cutting v2 slices (bell + brief); react-query shares these caches
  // with the surfaces, so this costs no extra requests while Command is open.
  const budget = useContract('budget', { pollMs: 60_000 });
  const approvals = useContract('approvals', { pollMs: 20_000 });
  const activity = useContract('activity', { pollMs: 30_000 });

  const incidents = (health.data as HealthTile[] | undefined) ?? [];
  const crewBadge = useMemo(() => (crew.data ?? []).filter((c) => c.status === 'running').length, [crew.data]);
  const reviewsBadge = reviews.data?.needsReply;

  const brief = useMemo(() => {
    const b = deriveDailyBrief({ health: health.data, budget: budget.data, approvals: approvals.data, activity: activity.data, crew: crew.data });
    return [b.headline, ...b.lines.map((l) => `• ${l.text}`)].join('\n');
  }, [health.data, budget.data, approvals.data, activity.data, crew.data]);

  // Global palette shortcuts: "/" or Cmd/Ctrl+K (never while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
      if ((e.key === '/' && !typing) || (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const paletteActions = useMemo<PaletteAction[]>(
    () => [
      { id: 'new-build', label: 'New build — ask the crew', hint: 'action · dispatch', run: () => setBuildOpen(true) },
      { id: 'talk', label: 'Talk to AI', hint: 'action · chat', run: () => setTalkOpen(true) },
      { id: 'customize', label: 'Customize widgets', hint: 'action · Command', run: () => { nav('/app/command'); setEditing(true); } },
      { id: 'theme', label: `Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`, hint: 'action · display', run: toggleTheme },
      { id: 'signout', label: 'Sign out', hint: 'action · session', run: signOut },
    ],
    [nav, setEditing, theme, toggleTheme, signOut],
  );

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font-body)' }}>
      <div className="mc-shell-grid">
        {!isMobile && (
          <Sidebar
            user={user}
            crewBadge={crewBadge || undefined}
            reviewsBadge={reviewsBadge}
            talkOpen={talkOpen}
            onOpenTalk={() => setTalkOpen(true)}
            onSignOut={signOut}
          />
        )}

        <main style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <Topbar
            label={labelForPath(pathname)}
            syncedAt={health.syncedAt}
            onNewBuild={() => setBuildOpen(true)}
            onOpenPalette={() => setPaletteOpen(true)}
            bell={<NotificationsBell approvals={approvals.data} health={health.data} budget={budget.data} activity={activity.data} />}
          />

          {/* Announce surface changes to assistive tech (SPA route swaps). */}
          <div aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
            {labelForPath(pathname)}
          </div>
          <div
            className="mc-scroll mc-content"
            tabIndex={0}
            role="region"
            aria-label={`${labelForPath(pathname)} content`}
            style={{ flex: 1, overflow: 'auto', padding: 'var(--mc-pad)', display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)', minHeight: 0 }}
          >
            {/* The red-alert ribbon lives on Command only (matches the prototype). */}
            {!alertDismissed && pathname === '/app/command' && (
              <AlertRibbon
                incidents={incidents}
                onInvestigate={(h) => pushToast('info', `Opening ${h.name}`, h.detail)}
                onDismiss={() => setAlertDismissed(true)}
              />
            )}
            <Outlet />
          </div>
        </main>
      </div>

      <MobileNav
        user={user}
        crewBadge={crewBadge || undefined}
        reviewsBadge={reviewsBadge}
        onOpenTalk={() => setTalkOpen(true)}
        onSignOut={signOut}
        onStartBuild={() => setBuildOpen(true)}
      />

      <TalkDock open={talkOpen} onClose={() => setTalkOpen(false)} seed={chat.data ?? PLACEHOLDER.chat} brief={brief} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} actions={paletteActions} />
      <NewBuildDialog open={buildOpen} onClose={() => setBuildOpen(false)} />
      <PwaNudge />
    </div>
  );
}

export function AppShell() {
  return (
    <WidgetEditProvider>
      <ShellInner />
    </WidgetEditProvider>
  );
}
